import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import * as Sentry from '@sentry/angular';
import { ApiResponse } from '@shared/models/api-response';
import { ReplicationSyncStatus } from '@shared/models/replication-sync-status';
import { SalesOrder, SyncSalesOrderResult } from '@shared/models/sales-order';
import { AppSettingsService } from '../app-settings.service';
import { CorePosService } from '../core-pos.service';
import { OnlineOfflineService } from '../online-offine.service';
import { SalesOrderReplicationService } from '../sales-order-replication.service';
import { SyncStatusManager } from './sync-status-manager.service';
import { classifySyncFailure, SyncFailure } from './sync-failure';

/**
 * Pushes locally-stored classic sales orders (Order 2.5) to the Edara 2
 * backend, sourced entirely from Firestore — never from the Desktop app.
 *
 * Each cycle reads the orders still pending sync, claims them as `Syncing` to
 * guard against concurrent double-sends (the Service Worker runs the same
 * pipeline), posts them to `v2.0/salesOrders/sync` in chunks keyed by
 * `external_id` (= `metadata.localId`), then marks each order `Synced` on
 * success or rolls it back for retry on failure. The legacy post-sync side
 * effects — promoting the offline customer and the automatic approve/issue of
 * locked orders — are preserved. The run is non-blocking and self-coalescing.
 *
 * @see US 27474 — Replicate & Restore (Order 2.5), Part 2
 */
@Injectable({ providedIn: 'root' })
export class SalesOrderSyncService {
  private readonly http = inject(HttpClient);
  private readonly onlineOfflineService = inject(OnlineOfflineService);
  private readonly salesOrderService = inject(SalesOrderReplicationService);
  private readonly syncStatusManager = inject(SyncStatusManager);
  private readonly corePosService = inject(CorePosService);

  private readonly edaraApiBaseUrl = AppSettingsService.appSettings.edaraApi.baseUrl;
  private readonly entityType = 'salesOrder';
  /** Matches the chunk size of the legacy sales-order push. */
  private static readonly CHUNK_SIZE = 10;
  private isSyncing = false;

  /**
   * Push every unsynced sales order to the backend. Non-blocking and
   * self-coalescing — safe to call from the periodic/login/online triggers.
   */
  async pushUnsyncedToServer(): Promise<void> {
    if (this.isSyncing || !this.onlineOfflineService.isOnline) return;

    this.isSyncing = true;
    try {
      const pending = await this.salesOrderService.selectForPushAsync(false);
      if (pending.length === 0) return;

      // Claim the records so a concurrent cycle cannot double-send them.
      const claimed = await this.syncStatusManager.applyStatus(
        this.entityType,
        pending,
        ReplicationSyncStatus.Syncing
      );

      for (let i = 0; i < claimed.length; i += SalesOrderSyncService.CHUNK_SIZE) {
        await this.syncChunk(claimed.slice(i, i + SalesOrderSyncService.CHUNK_SIZE));
      }
    } catch (error) {
      console.error('[SalesOrderSync] Push failed:', error);
      Sentry.captureException(error);
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Push specific orders on demand — the orders grid "Sync" action and the
   * data module's re-sync — through the very same claim → POST → verdict
   * pipeline as the background cycle: the Desktop replica is mirrored on every
   * transition, an order the backend already accepted is never downgraded,
   * and the post-sync side effects run. The passed objects are updated in
   * place (status, `document_code`), so a grid row reflects the outcome
   * without a refetch. Returns the backend's per-order verdicts (empty when
   * offline or when the request never produced any).
   */
  async pushOrdersAsync(salesOrders: SalesOrder[]): Promise<SyncSalesOrderResult[]> {
    if (!salesOrders?.length || !this.onlineOfflineService.isOnline) return [];

    const claimed = await this.syncStatusManager.applyStatus(
      this.entityType,
      salesOrders,
      ReplicationSyncStatus.Syncing
    );

    const results: SyncSalesOrderResult[] = [];
    for (let i = 0; i < claimed.length; i += SalesOrderSyncService.CHUNK_SIZE) {
      results.push(...await this.syncChunk(claimed.slice(i, i + SalesOrderSyncService.CHUNK_SIZE)));
    }
    return results;
  }

  private async syncChunk(salesOrders: SalesOrder[]): Promise<SyncSalesOrderResult[]> {
    try {
      const response = await firstValueFrom(
        this.http.post<ApiResponse<SyncSalesOrderResult[]>>(
          `${this.edaraApiBaseUrl}v2.0/salesOrders/sync`,
          this.buildSyncPayload(salesOrders)
        )
      );

      const results = this.extractSyncResults(response?.result);
      if (response?.status_code === 200) {
        await this.applyResults(salesOrders, results);
      } else {
        // The backend answered but refused the request as a whole. Any
        // per-order verdict it still carried is honoured; the rest inherit the
        // envelope failure (500-class, so it keeps retrying automatically).
        await this.applyResults(salesOrders, results, {
          status: ReplicationSyncStatus.Failed,
          message: response?.error_message ?? `Backend responded ${response?.status_code}`
        });
      }
      return results;
    } catch (error: any) {
      // Chunk-level failure: a backend rejection becomes `Failed` (with the
      // reason), while a request that never reached the backend stays
      // `UnSynced`. Both are retried — `external_id` idempotency makes a
      // re-send safe even if the request actually landed. Per-order verdicts
      // found in the error body are honoured first, so accepted orders are
      // never buried under a neighbour's rejection.
      const failure = classifySyncFailure(error);
      const results = this.extractSyncResults(error?.error?.result ?? error?.error);
      await this.applyResults(salesOrders, results, failure);
      console.error(
        `[SalesOrderSync] Backend sync failed for ${salesOrders.length} order(s); unacknowledged ones marked ${failure.status}.`,
        error
      );
      Sentry.captureException(error);
      return results;
    }
  }

  /**
   * The wire payload: minified clones, exactly like the legacy push —
   * `minify()` strips `id`/`warehouse`/`salesperson`/`metadata`/
   * `cashRegister` and the per-item local fields.
   */
  private buildSyncPayload(salesOrders: SalesOrder[]): SalesOrder[] {
    return salesOrders.map(order => SalesOrder.clone(order).minify());
  }

  /**
   * Read the per-order verdicts out of a sync response body or error body.
   * Anything that is not a recognisable result list yields `[]`, so the caller
   * falls back to its chunk-level verdict.
   */
  private extractSyncResults(body: any): SyncSalesOrderResult[] {
    const candidate = Array.isArray(body)
      ? body
      : body?.result ?? body?.results ?? body?.data;
    if (!Array.isArray(candidate)) return [];
    return candidate.filter(
      (item: any) => !!item && typeof item.external_id === 'string' && item.status_code != null
    );
  }

  /**
   * Apply the per-order backend outcome. An order the backend rejected becomes
   * `Failed` (carrying the reason) so the orders grid can surface it; an order
   * simply missing from the response falls back to `unacknowledged` — by
   * default `UnSynced`, since that is a protocol gap rather than a rejection,
   * or the caller's chunk-level verdict when the whole request failed.
   */
  private async applyResults(
    salesOrders: SalesOrder[],
    results: SyncSalesOrderResult[],
    unacknowledged: SyncFailure = {
      status: ReplicationSyncStatus.UnSynced,
      message: 'The backend did not acknowledge the order'
    }
  ): Promise<void> {
    const resultsByExternalId = new Map(results.map(result => [result.external_id, result]));

    const succeeded: SalesOrder[] = [];
    const rejectedByMessage = new Map<string, SalesOrder[]>();
    const unanswered: SalesOrder[] = [];

    for (const salesOrder of salesOrders) {
      const result = resultsByExternalId.get(salesOrder.external_id!);
      if (!result) {
        unanswered.push(salesOrder);
        continue;
      }
      if (result.status_code === true) {
        // The document code and the auto work-order flag the backend decided
        // ride into Firestore with the `Synced` status write (see
        // additionalStatusFields). The flag gates the SO lock automatic
        // approve/issue actions, so dropping it would let them fire on an
        // automatic order.
        salesOrder.document_code = result.document_code ?? salesOrder.document_code;
        salesOrder.require_auto_workorder = result.require_auto_workorder ?? salesOrder.require_auto_workorder;
        succeeded.push(salesOrder);
        continue;
      }
      const message = result.status_message ?? 'The backend rejected the order';
      const bucket = rejectedByMessage.get(message) ?? [];
      bucket.push(salesOrder);
      rejectedByMessage.set(message, bucket);
    }

    if (succeeded.length) {
      // Empty message, not `undefined`: `applyStatus` leaves the field alone
      // when nothing is passed, which would strand an earlier rejection's text
      // on an order that has since been accepted.
      await this.syncStatusManager.applyStatus(
        this.entityType,
        succeeded,
        ReplicationSyncStatus.Synced,
        ''
      );
    }

    for (const [message, group] of rejectedByMessage) {
      await this.applyFailureStatus(group, ReplicationSyncStatus.Failed, message);
      console.error(
        `[SalesOrderSync] ${group.length} order(s) rejected by the backend; marked Failed: ${message}`
      );
    }

    if (unanswered.length) {
      await this.applyFailureStatus(unanswered, unacknowledged.status, unacknowledged.message);
      console.warn(
        `[SalesOrderSync] ${unanswered.length} order(s) missing from the sync response; marked ${unacknowledged.status}.`
      );
    }

    // Legacy post-sync side effects, keyed off the per-order verdicts (both
    // helpers skip non-success results internally): promote the offline
    // customer into the local DB and run the automatic approve/issue actions
    // configured for locked orders.
    if (results.length) {
      try {
        this.corePosService.updateCustomersSyncStatus(salesOrders, results);
        await this.corePosService.processAutomaticActionsForLockedOrders(results);
      } catch (error) {
        console.error('[SalesOrderSync] Post-sync side effects failed:', error);
        Sentry.captureException(error);
      }
    }
  }

  /**
   * Apply a non-success verdict without ever downgrading an order the backend
   * has already accepted. `Synced` is terminal, but the same orders are pushed
   * by two pipelines (this service and the Service Worker) whose responses can
   * land out of order, so a late failure verdict could otherwise overwrite a
   * record that is already `Synced`. The Firestore re-read is best-effort and
   * only runs on the failure path.
   */
  private async applyFailureStatus(
    salesOrders: SalesOrder[],
    status: ReplicationSyncStatus,
    message: string
  ): Promise<void> {
    const candidates = salesOrders.filter(
      salesOrder => salesOrder.metadata?.syncInfo?.syncStatus !== ReplicationSyncStatus.Synced
    );

    const checked = await Promise.all(
      candidates.map(async salesOrder => {
        try {
          const stored = await this.salesOrderService.getByLocalIdAsync(
            salesOrder.metadata!.localId
          );
          return stored?.metadata?.syncInfo?.syncStatus === ReplicationSyncStatus.Synced
            ? null
            : salesOrder;
        } catch {
          return salesOrder;
        }
      })
    );

    const toApply = checked.filter((salesOrder): salesOrder is SalesOrder => !!salesOrder);
    const skipped = salesOrders.length - toApply.length;
    if (skipped > 0) {
      console.warn(
        `[SalesOrderSync] Skipped '${status}' for ${skipped} order(s) already accepted by the backend.`
      );
    }
    if (!toApply.length) return;

    await this.syncStatusManager.applyStatus(this.entityType, toApply, status, message);
  }
}
